import { cn } from '@aw/lib/utils/cn'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

const headingVariants = cva("font-montserrat text-foreground", {
    variants: {
        size: {
            h1: "text-[30px] leading-[38px] lg:text-[32px] lg:leading-[40px] xl:text-[36px] xl:leading-[44px]",
            h2: "text-[26px] leading-[34px] lg:text-[28px] lg:leading-[36px] xl:text-[32px] xl:leading-[40px]",
            h3: "text-[24px] leading-[30px] lg:leading-[32px] xl:text-[28px] xl:leading-[36px]",
            h4: "text-[20px] leading-[28px] lg:text-[22px] xl:text-[24px] xl:leading-[32px]",
            h5: "text-[18px] leading-[24px] lg:leading-[26px] xl:text-[20px] xl:leading-[28px]",
            h6: "text-[14px] leading-[20px] lg:text-[15px] lg:leading-[22px] xl:text-[16px] xl:leading-[24px]",
        },
        weight: {
            regular: "font-normal",
            semibold: "font-semibold",
            bold: "font-bold",
        },
    },
    defaultVariants: {
        size: "h1",
        weight: "bold",
    },
})

type HeadingElement = "h1" | "h2" | "h3" | "h4" | "h5" | "h6"

interface HeadingProps
    extends React.ComponentProps<"h1">,
        VariantProps<typeof headingVariants> {
    as?: HeadingElement
    children?: React.ReactNode
    className?: string
    testId?: string
}

function Heading({ size = "h1", weight, as, className, children, testId, ...props }: HeadingProps) {
    const Component = as ?? size ?? "h1"

    return (
        <Component
            data-testid={testId}
            className={cn(headingVariants({ size, weight, className }))}
            {...props}
        >
            {children}
        </Component>
    )
}

type HeadingSize = VariantProps<typeof headingVariants>["size"]
type HeadingWeight = VariantProps<typeof headingVariants>["weight"]

export default Heading
export { headingVariants }
export type { HeadingSize, HeadingWeight, HeadingProps }